import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Pagination from "@mui/material/Pagination";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Group, Panel, Separator } from "react-resizable-panels";
import { useKeepAliveContext } from "keepalive-for-react";

import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import MDAvatar from "../../../../components/MDAvatar";
import MDButton from "../../../../components/MDButton";
import PreviewTabs from "../../../../components/PreviewTabs/PreviewTabs";
import { onErrorAlert, onSuccessAlert } from "../../../../actions";

const displayValue = (value) => (value !== null && value !== undefined && value !== "" ? value : "-");
const qtyValue = (value) => value ?? 0;
const formatDate = (value) => value ? moment(value).format("YYYY-MM-DD") : "-";
const formatDateTime = (value) => value ? moment(value).format("MMM DD, YYYY HH:mm (z)") : "-";
const firstValue = (...values) => values.find((value) => value !== null && value !== undefined && value !== "");

const detailGridSx = {
    "& > .MuiGrid-item": {
        minWidth: 0,
    },
    "@media (min-width: 900px)": {
        "& > .MuiGrid-item": {
            flexBasis: "50%",
            maxWidth: "50%",
        },
    },
    "@media (min-width: 1200px)": {
        "& > .MuiGrid-item": {
            flexBasis: "33.333333%",
            maxWidth: "33.333333%",
        },
    },
    "@media (min-width: 1536px)": {
        "& > .MuiGrid-item": {
            flexBasis: "20%",
            maxWidth: "20%",
        },
    },
};

const useCopy = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return (text) => {
        if (!text && text !== 0) return;
        navigator.clipboard?.writeText(String(text))
            .then(() => dispatch(onSuccessAlert(`"${text}" ${t("copied")}`)))
            .catch(() => dispatch(onErrorAlert(t("copy failed"))));
    };
};

const FieldText = ({ children }) => (
    <MDTypography
        variant="button"
        color="text"
        fontWeight="regular"
        sx={{
            lineHeight: 1.25,
            display: "block",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
        }}
    >
        {displayValue(children)}
    </MDTypography>
);

const Field = ({ label, value, copyText }) => {
    const copy = useCopy();

    return (
        <MDBox sx={{ pr: 1, minWidth: 0 }}>
            <MDBox
                sx={{
                    display: "flex",
                    alignItems: "center",
                    minHeight: 20,
                    mb: 0.75,
                    minWidth: 0,
                }}
            >
                <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{
                        lineHeight: 1.15,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        minWidth: 0,
                    }}
                >
                    {label}:
                </MDTypography>
                {!!copyText && (
                    <Tooltip title="Copy" placement="right">
                        <IconButton size="small" sx={{ p: 0, ml: 0.3 }} onClick={() => copy(copyText)}>
                            <ContentCopyIcon sx={{ width: 12, height: 12 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </MDBox>
            <FieldText>{value}</FieldText>
        </MDBox>
    );
};

const Section = ({ title, children, first = false }) => (
    <MDBox
        sx={{
            borderTop: first ? "none" : "1px solid",
            borderColor: "divider",
            pt: first ? 0.25 : 0.75,
            mt: first ? 0 : 0.75,
        }}
    >
        <MDTypography
            variant="button"
            fontWeight="bold"
            color="primary"
            sx={{ display: "block", mb: 0.5 }}
        >
            {title}
        </MDTypography>
        {children}
    </MDBox>
);

const QtyChip = ({ label, value, color = "default", variant = "outlined" }) => (
    <Chip
        size="small"
        label={`${label}: ${qtyValue(value)}`}
        color={color}
        variant={variant}
        sx={{
            height: 18,
            fontSize: 10,
            fontWeight: 700,
            maxWidth: 132,
            "& .MuiChip-label": {
                px: 0.75,
                overflow: "hidden",
                textOverflow: "ellipsis",
            },
        }}
    />
);

const MetaLine = ({ label, value }) => (
    <MDTypography
        variant="caption"
        color="text"
        sx={{
            display: "block",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.25,
        }}
    >
        <span style={{ fontWeight: 700 }}>{label}: </span>{displayValue(value)}
    </MDTypography>
);

const StockListItem = ({ item, selected, onClick, type, isMobile }) => {
    const { t } = useTranslation();
    const copy = useCopy();
    const allocatedQty = item?.omsQtyAllocated ?? item?.qtyAllocated;
    const onHoldQty = item?.omsQtyOnHold ?? item?.qtyOnHold;

    return (
        <MDBox
            onClick={onClick}
            sx={{
                p: 1,
                mb: 0.8,
                borderRadius: 1.5,
                cursor: "pointer",
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? 0 : 320,
                maxWidth: isMobile ? "none" : 400,
                backgroundColor: selected ? "action.selected" : "transparent",
                border: "1px solid",
                borderColor: selected ? "primary.main" : "divider",
                transition: "all 0.15s",
                "&:hover": { backgroundColor: "action.hover" },
            }}
        >
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <MDTypography variant="button" fontWeight="medium" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item?.sku || "-"}
                    </MDTypography>
                    {!!item?.sku && (
                        <IconButton
                            size="small"
                            sx={{ p: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                copy(item.sku);
                            }}
                        >
                            <ContentCopyIcon sx={{ width: 14, height: 14 }} />
                        </IconButton>
                    )}
                </MDBox>

                <QtyChip label={t("InventoryStockPage.Available")} value={item?.qtyAvailable} color={qtyValue(item?.qtyAvailable) > 0 ? "success" : "warning"} variant="filled" />
            </MDBox>

            <MDBox mt={0.5}>
                <MDBox sx={{ minWidth: 0 }}>
                    <MDTypography variant="caption" color="primary" sx={{ display: "block", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item?.productName || "-"}
                    </MDTypography>
                    <MetaLine label={t("ProductPage.skuAlias")} value={item?.skuAlias} />
                    {type === "batch" && <MetaLine label={t("InventoryStockPage.Batch No.")} value={item?.batchNo} />}
                </MDBox>

            </MDBox>

            <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mt={1} sx={{ flexWrap: "wrap" }}>
                <MDBox display="flex" alignItems="center" gap={0.8} sx={{ minWidth: 0, flex: "1 1 145px" }}>
                    <MDAvatar bgColor="light" size="sm">
                        {item?.ownerName?.[0] || item?.ownerId?.[0] || "S"}
                    </MDAvatar>
                    <MDBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
                        <MDTypography variant="caption" fontWeight="medium" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item?.ownerId || "-"}
                        </MDTypography>
                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item?.ownerName || "-"}
                        </MDTypography>
                    </MDBox>
                </MDBox>

                <MDBox sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0, justifyContent: "flex-end", flex: "1 1 155px", ml: "auto" }}>
                    <MDTypography
                        variant="caption"
                        sx={(theme) => ({
                            display: "inline-block",
                            color: theme.palette.info.main,
                            background: theme.palette.info.lighter,
                            px: 1,
                            py: 0.2,
                            maxWidth: "100%",
                            borderRadius: "6px",
                            fontWeight: 600,
                            lineHeight: 1.25,
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            textAlign: "right",
                        })}
                    >
                        {item?.warehouseDesc || "-"}
                    </MDTypography>
                    {type === "batch" && !!item?.qualityCode && (
                        <Chip size="small" label={item.qualityCode} variant="outlined" sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0 }} />
                    )}
                </MDBox>
            </MDBox>

            <MDBox mt={0.75} display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                <QtyChip label={t("InventoryStockPage.Total Qty")} value={item?.qtyTotal} />
                <QtyChip label={t("InventoryStockPage.Allocated")} value={allocatedQty} />
                <QtyChip label={t("InventoryStockPage.On Hold")} value={onHoldQty} />
            </MDBox>
        </MDBox>
    );
};

const StockListPanel = ({
    data,
    total,
    pagination,
    setPagination,
    selectedRow,
    setSelectedRow,
    setStage,
    type,
    isMobile,
    onOpenFilter,
    activeFilterCount = 0,
    onResetFilters,
    filterDisabled = false,
}) => {
    const { t } = useTranslation();

    return (
        <MDBox sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
            <MDBox px={1} pt={isMobile ? 0.75 : 0} pb={isMobile ? 0.75 : 0}>
                <MDBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    <MDTypography variant="h6" sx={{ lineHeight: 1.2 }}>
                        {type === "batch" ? t("InventoryStockPage.Search Batch Information") : t("InventoryStockPage.Search Product Information")}{" "}
                        <span style={{ fontWeight: 400, color: "var(--color-text-tertiary)", fontSize: "0.85em" }}>({total})</span>
                    </MDTypography>
                    {isMobile && (
                        <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, overflow: "visible" }}>
                            <MDBox sx={{ position: "relative", display: "inline-flex", overflow: "visible" }}>
                                <MDButton
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    startIcon={<Icon>filter_list</Icon>}
                                    onClick={onOpenFilter}
                                    disabled={filterDisabled}
                                    sx={{
                                        borderRadius: "6px",
                                        minWidth: 86,
                                        px: 1.25,
                                        py: 0.4,
                                        overflow: "visible",
                                        "& .material-icons": {
                                            fontSize: "16px !important",
                                        },
                                    }}
                                >
                                    Filter
                                </MDButton>
                                {activeFilterCount > 0 && (
                                    <MDBox
                                        sx={{
                                            position: "absolute",
                                            top: -9,
                                            right: -5,
                                            minWidth: 18,
                                            height: 18,
                                            px: 0.5,
                                            borderRadius: "999px",
                                            bgcolor: "info.main",
                                            color: "#fff !important",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            lineHeight: "18px",
                                            textAlign: "center",
                                            boxShadow: "0 0 0 2px #fff",
                                        }}
                                    >
                                        {activeFilterCount}
                                    </MDBox>
                                )}
                            </MDBox>
                            {activeFilterCount > 0 && (
                                <MDButton
                                    color="secondary"
                                    variant="text"
                                    size="small"
                                    onClick={onResetFilters}
                                    disabled={filterDisabled}
                                    sx={{ minWidth: 52, px: 0.75 }}
                                >
                                    Reset
                                </MDButton>
                            )}
                        </MDBox>
                    )}
                </MDBox>
            </MDBox>

            <MDBox sx={{ flex: 1, overflowY: "auto", minHeight: 0, px: 1, pb: isMobile ? 0.5 : 0 }}>
                {data.map((item) => (
                    <StockListItem
                        key={item.id}
                        item={item}
                        selected={selectedRow?.id === item?.id}
                        type={type}
                        isMobile={isMobile}
                        onClick={() => {
                            setSelectedRow(item);
                            if (isMobile) setStage("detail");
                        }}
                    />
                ))}
            </MDBox>

            <MDBox sx={{ position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid #eee", py: 1, display: "flex", justifyContent: "center", zIndex: 2 }}>
                <Pagination
                    size="small"
                    count={Math.ceil(total / pagination.pageSize)}
                    page={pagination.pageIndex + 1}
                    onChange={(e, page) => setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))}
                />
            </MDBox>
        </MDBox>
    );
};

const StockDetailPanel = ({ data, loading, setStage, type, isMobile }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("main");
    const addUser = firstValue(data?.addWho, data?.addUser, data?.createBy, data?.creator);
    const addTime = firstValue(data?.addTime, data?.createTime, data?.createdTime);
    const editUser = firstValue(data?.editWho, data?.editUser, data?.updateBy, data?.updater);
    const editTime = firstValue(data?.editTime, data?.updateTime, data?.updatedTime);
    const hasOtherInfo = !!(addUser || addTime || editUser || editTime);
    const tabs = [
        { value: "main", label: "Main Info" },
        ...(type === "batch" ? [{ value: "batch", label: "Batch Info" }] : []),
        ...(hasOtherInfo ? [{ value: "other", label: "Other" }] : []),
    ];

    useEffect(() => {
        if (!tabs.some((tab) => tab.value === activeTab)) {
            setActiveTab("main");
        }
    }, [activeTab, tabs]);

    const mainContent = (
        <MDBox sx={{ minWidth: 250, pt: 0, pb: 0.5, px: 2 }}>
            <Section title="Product Details" first>
                <Grid container columns={10} spacing={0.75} sx={detailGridSx}>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ProductPage.sku")} value={data?.sku} copyText={data?.sku} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ProductPage.skuAlias")} value={data?.skuAlias} copyText={data?.skuAlias} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ProductPage.productName")} value={data?.productName} copyText={data?.productName} />
                    </Grid>
                </Grid>
            </Section>

            <Section title="Warehouse">
                <Grid container columns={10} spacing={0.75} sx={detailGridSx}>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("Warehouse")} value={data?.warehouseDesc} copyText={data?.warehouseDesc} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ownerId")} value={data?.ownerId} copyText={data?.ownerId} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ownerName")} value={data?.ownerName} copyText={data?.ownerName} />
                    </Grid>
                </Grid>
            </Section>

            <Section title="Inventory">
                <Grid container columns={10} spacing={0.75} sx={detailGridSx}>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Total Qty")} value={qtyValue(data?.qtyTotal)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Available")} value={qtyValue(data?.qtyAvailable)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Allocated")} value={qtyValue(data?.omsQtyAllocated ?? data?.qtyAllocated)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.On Hold")} value={qtyValue(data?.omsQtyOnHold ?? data?.qtyOnHold)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Control Inventory")} value={data?.controlInventory} />
                    </Grid>
                </Grid>
            </Section>

            <Section title={t("InventoryStockPage.Remark")}>
                <Grid container columns={10} spacing={0.75} sx={detailGridSx}>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Remark")} value={data?.remark} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ProductPage.Attribute 1")} value={data?.attribute1} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("ProductPage.Attribute 2")} value={data?.attribute2} />
                    </Grid>
                </Grid>
            </Section>
        </MDBox>
    );

    const batchContent = (
        <MDBox sx={{ minWidth: 250, pt: 0, pb: 0.5, px: 2 }}>
            <Section title="Batch Details" first>
                <Grid container columns={10} spacing={0.75} sx={detailGridSx}>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Batch No.")} value={data?.batchNo} copyText={data?.batchNo} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Quality Status")} value={data?.qualityCode} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Production")} value={formatDate(data?.productionDate)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Expiration")} value={formatDate(data?.expirationDate)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Warehousing")} value={formatDate(data?.warehousingDate)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("InventoryStockPage.Supplier")} value={data?.supplierName} />
                    </Grid>
                </Grid>
            </Section>
        </MDBox>
    );

    const otherContent = (
        <MDBox sx={{ minWidth: 250, pt: 0, pb: 0.5, px: 2 }}>
            <Section title={t("InventoryStockPage.Operation Records")} first>
                <Grid container columns={10} spacing={0.75} sx={detailGridSx}>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("Add User")} value={addUser} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("Add Time")} value={formatDateTime(addTime)} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("Edit User")} value={editUser} />
                    </Grid>
                    <Grid item xs={10} sm={5} md={2}>
                        <Field label={t("Edit Time")} value={formatDateTime(editTime)} />
                    </Grid>
                </Grid>
            </Section>
        </MDBox>
    );

    return (
        <MDBox sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <MDBox px={isMobile ? 1 : 2} py={1}>
                <MDBox sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    {isMobile && (
                        <Tooltip title={t("Back")}>
                            <IconButton size="small" onClick={() => setStage("list")} sx={{ width: 34, height: 34, border: "1px solid", borderColor: "divider", borderRadius: "8px", flexShrink: 0 }}>
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <MDTypography variant="h6" fontWeight="bold" noWrap sx={{ lineHeight: 1, margin: 0, minWidth: 0 }}>
                        {data?.sku || t("No Data")}
                    </MDTypography>
                </MDBox>
            </MDBox>

            <MDBox sx={{ borderTop: "1px solid #eee" }} />

            <MDBox sx={{ flex: 1, minHeight: 0, px: isMobile ? 1 : 2, py: 1, overflow: "hidden" }}>
                {loading ? (
                    <MDBox sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <CircularProgress size={28} />
                    </MDBox>
                ) : !data?.id ? (
                    <MDBox sx={{ height: "100%", minHeight: 260, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "text.secondary", textAlign: "center", gap: 0.5 }}>
                        <Icon sx={{ fontSize: 42, color: "text.disabled" }}>inventory_2</Icon>
                        <MDTypography variant="h6" color="text">
                            {t("No Data")}
                        </MDTypography>
                    </MDBox>
                ) : (
                    <MDBox sx={{ height: "100%", minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                        <PreviewTabs
                            value={activeTab}
                            onChange={(value) => setActiveTab(value)}
                            tabs={tabs}
                        />

                        <MDBox sx={{ flex: 1, overflowY: "auto", minHeight: 0, pt: 0.5 }}>
                            {activeTab === "main" && mainContent}
                            {activeTab === "batch" && batchContent}
                            {activeTab === "other" && otherContent}
                        </MDBox>
                    </MDBox>
                )}
            </MDBox>
        </MDBox>
    );
};

const StockCard = ({
    data = [],
    total,
    pagination,
    setPagination,
    tableHeight,
    viewMode,
    loading,
    type = "product",
    onOpenFilter,
    activeFilterCount = 0,
    onResetFilters,
}) => {
    const { active: isTabActive } = useKeepAliveContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedRow, setSelectedRow] = useState(null);
    const [showList, setShowList] = useState(true);
    const [stage, setStage] = useState("list");
    const isMountedRef = useRef(false);

    useEffect(() => {
        if (!data?.length || viewMode !== "card") {
            setSelectedRow(null);
            return;
        }

        if (!selectedRow || !data.some((item) => item.id === selectedRow.id)) {
            setSelectedRow(data[0]);
        }
    }, [data, viewMode]);

    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        setSelectedRow(null);
        setStage("list");
    }, [pagination.pageIndex]);

    const listPanel = (
        <StockListPanel
            data={data}
            total={total}
            pagination={pagination}
            setPagination={setPagination}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            setStage={setStage}
            type={type}
            isMobile={isMobile}
            onOpenFilter={onOpenFilter}
            activeFilterCount={activeFilterCount}
            onResetFilters={onResetFilters}
            filterDisabled={loading}
        />
    );

    const detailPanel = <StockDetailPanel data={selectedRow} loading={loading} setStage={setStage} type={type} isMobile={isMobile} />;

    if (isMobile) {
        return (
            <MDBox sx={{ height: tableHeight, display: "flex", overflow: "hidden" }}>
                <Grid container sx={{ height: "100%", minHeight: 0 }}>
                    {stage === "list" && <Grid item xs={12} sx={{ height: "100%", minHeight: 0 }}>{listPanel}</Grid>}
                    {stage === "detail" && <Grid item xs={12} sx={{ height: "100%", minHeight: 0 }}>{detailPanel}</Grid>}
                </Grid>
            </MDBox>
        );
    }

    return (
        <MDBox sx={{ height: tableHeight, display: "flex", overflow: "hidden" }}>
            {isTabActive && (
                <Group direction="horizontal" style={{ height: "100%", width: "100%", minHeight: 0 }}>
                    {showList && (
                        <>
                            <Panel defaultSize={25} minSize={20}>
                                <MDBox sx={{ height: "100%", minHeight: 0, boxSizing: "border-box", py: 2, pl: 2, overflow: "hidden" }}>
                                    {listPanel}
                                </MDBox>
                            </Panel>

                            <Separator style={{ width: "1px", position: "relative", cursor: "col-resize", background: "transparent" }}>
                                <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: "2px", transform: "translateX(-50%)", background: "#d1d5db" }} />
                            </Separator>
                        </>
                    )}

                    <Panel defaultSize={75} minSize={40}>
                        <MDBox sx={{ height: "100%", minHeight: 0, boxSizing: "border-box", overflow: "hidden" }}>
                            {detailPanel}
                        </MDBox>
                    </Panel>
                </Group>
            )}
        </MDBox>
    );
};

export default StockCard;
